import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import type { BlocksContent } from '@strapi/blocks-react-renderer';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { CAMPAIGN_PRICE, CAMPAIGN_PRICE_RICHTEXT } from '../priceRichText';
import { PricingSlot } from './PricingSlot';
import type { CompactPricingCardConfig, CompactPricingResolved } from './types';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { resolvePrice } from '@/lib/pricing/resolvePrice';
import { hasRichTextContent } from '@/lib/render/hasRichTextContent';
import { imageAlt } from '@/lib/render/imageAlt';
import { useRenderContext } from '@/lib/render/RenderContextProvider';
import { RichText } from '@/lib/render/RichText';
import { substituteHref } from '@/lib/render/substituteHref';
import { useRuntimeConfig } from '@/lib/runtime-config/RuntimeConfigProvider';

function SingleCard({
  header,
  price,
  fixedPrice,
  pricePrefix,
  campaignPricing,
  ctaUrl,
  onCtaClick,
}: {
  header: string;
  price: string | undefined;
  /** Literal price, shown when `price` did not resolve (SH-415). */
  fixedPrice?: BlocksContent | undefined;
  pricePrefix: string;
  /** Campaign variant B's price typography — see `../priceRichText`. */
  campaignPricing?: boolean;
  ctaUrl?: string;
  onCtaClick?: () => void;
}) {
  return (
    <PricingSlot
      ctaUrl={ctaUrl}
      onClick={onCtaClick}
      className="bg-secondary-lighter flex flex-1 flex-col items-center justify-center gap-2 rounded-xl px-3 py-6 text-center md:p-6"
    >
      <p className="text-primary m-0 text-sm leading-none font-bold uppercase md:text-base">
        {header}
      </p>
      {price ? (
        <p
          className={classNames(
            'm-0 whitespace-nowrap',
            campaignPricing
              ? CAMPAIGN_PRICE
              : 'text-primary-dark text-lg leading-none font-medium md:text-2xl',
          )}
        >
          {pricePrefix} {price}
        </p>
      ) : (
        fixedPrice && (
          // A div, not a p: the richtext renders its own block-level
          // paragraphs. No `pricePrefix` — the editor's text is already
          // complete and formatted.
          <div
            className={classNames(
              campaignPricing
                ? CAMPAIGN_PRICE_RICHTEXT
                : 'text-primary-dark text-lg leading-none font-medium md:text-2xl [&_p]:m-0',
            )}
          >
            <RichText nodes={fixedPrice} />
          </div>
        )
      )}
    </PricingSlot>
  );
}

// The widget has no width container of its own and may sit directly in a
// gradient section, which has none either — so a card can be as wide as the
// viewport divided by the cards sharing its md+ row, and no narrower bound is
// safe. Stacked below md, each card takes the full width.
export function compactPricingImageSizes(cardCount: number): string {
  if (cardCount <= 1) {
    return '100vw';
  }
  return `(max-width: 767px) 100vw, ${Math.ceil(100 / cardCount)}vw`;
}

function CompactPricingCard({
  card,
  campaignPricing,
  imageSizes,
}: {
  card: CompactPricingCardConfig;
  campaignPricing?: boolean;
  imageSizes: string;
}) {
  const {
    entities,
    imageUrl,
    substitutePlaceholders: sub,
  } = useRenderContext();
  const t = useTranslations('compactPricing');
  const { brand: domainKey } = useRuntimeConfig();
  const price1 =
    resolvePrice(entities.location, card.pricePlaceholder, domainKey) ??
    undefined;
  const price2 = card.secondPricePlaceholder
    ? (resolvePrice(
        entities.location,
        card.secondPricePlaceholder,
        domainKey,
      ) ?? undefined)
    : undefined;
  // Literal fallbacks, used per slot when the placeholder is empty or
  // resolves to nothing (SH-415).
  const fixedPrice1 = hasRichTextContent(card.fixedPrice)
    ? card.fixedPrice
    : undefined;
  const fixedPrice2 = hasRichTextContent(card.secondFixedPrice)
    ? card.secondFixedPrice
    : undefined;

  // CMS text fields carry `{location.*}` / `{city.*}` tokens (e.g.
  // `{location.matelsoPhoneLabel}` in a badge or plan header). Nothing
  // upstream substitutes them — `resolveSync` is identity — so each field
  // has to go through the render context here, like every other widget.
  const badge = card.badge ? sub(card.badge) : undefined;
  const planHeader = sub(card.planHeader);
  const secondPlanHeader = card.secondPlanHeader
    ? sub(card.secondPlanHeader)
    : undefined;
  const ctaUrl = substituteHref(card.ctaUrl, sub) || undefined;
  const secondCtaUrl = substituteHref(card.secondCtaUrl, sub) || undefined;

  const hasTwoCards = Boolean(secondPlanHeader && (price2 || fixedPrice2));
  const imgSrc = imageUrl(card.image);
  // The card's header photo is content, not decoration: editors name it on
  // the media itself.
  const imgAlt = sub(imageAlt(card.image));

  const handleCtaClick = (label: string) => () =>
    pushCmsEventTracking(card.tracking, { label });

  return (
    <div className="relative flex-1">
      {imgSrc && (
        <div className="relative aspect-[349/131] w-full">
          <Image
            src={imgSrc}
            alt={imgAlt}
            fill
            sizes={imageSizes}
            className="rounded-t-xl border border-white object-cover"
          />
        </div>
      )}
      {badge && (
        <span className="text-primary-dark absolute top-0 left-1/2 z-10 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--color-light-blue)] px-4 py-1.5 text-sm font-semibold whitespace-nowrap uppercase">
          {badge}
        </span>
      )}
      {hasTwoCards ? (
        <div className="flex h-full gap-2 md:gap-[15px]">
          <SingleCard
            header={planHeader}
            price={price1}
            fixedPrice={fixedPrice1}
            pricePrefix={t('pricePrefix')}
            campaignPricing={campaignPricing}
            ctaUrl={ctaUrl}
            onCtaClick={handleCtaClick(planHeader)}
          />
          <SingleCard
            header={secondPlanHeader!}
            price={price2}
            fixedPrice={fixedPrice2}
            pricePrefix={t('pricePrefix')}
            campaignPricing={campaignPricing}
            ctaUrl={secondCtaUrl}
            onCtaClick={handleCtaClick(secondPlanHeader!)}
          />
        </div>
      ) : (
        <SingleCard
          header={planHeader}
          price={price1}
          fixedPrice={fixedPrice1}
          pricePrefix={t('pricePrefix')}
          campaignPricing={campaignPricing}
          ctaUrl={ctaUrl}
          onCtaClick={handleCtaClick(planHeader)}
        />
      )}
    </div>
  );
}

export function CompactPricingComponent({
  resolved,
  campaignPricing,
}: {
  resolved: CompactPricingResolved;
  /**
   * Campaign variant B's price typography. Set by
   * `CompactPricingComponentB`; variants A and C leave it unset and keep the
   * styling they had before SH-415.
   */
  campaignPricing?: boolean;
}) {
  const cards = resolved.cards ?? [];

  if (cards.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-6 md:flex-row md:items-stretch md:gap-4">
      {cards.map((card, i) => (
        <CompactPricingCard
          key={`${card.planHeader}-${i}`}
          card={card}
          campaignPricing={campaignPricing}
          imageSizes={compactPricingImageSizes(cards.length)}
        />
      ))}
    </div>
  );
}
